import { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import clsx from 'clsx';
import {
  categories,
  getCategory,
  getToolsByCategory,
  type ToolDef,
} from '@/tools/registry';

/**
 * Contextual sidebar block shown only on `/tools/*` paths.
 *
 * Behaviour:
 *   - On `/tools` (overview): list all 7 category links.
 *   - On `/tools/:category` (or deeper): list the tools in that
 *     category (available are NavLinks, planned are NavLinks too
 *     but greyed out — they still resolve to ToolPlaceholder).
 *
 * Always topped by a "← 返回工具广场" affordance so the user can
 * leave a category without having to navigate via the topbar.
 */
export function DevToolsNav() {
  const location = useLocation();
  const cat = useMemo(() => {
    const m = /^\/tools\/([^/]+)/.exec(location.pathname);
    return m ? getCategory(m[1]) : undefined;
  }, [location.pathname]);

  return (
    <div className="mt-3 px-1">
      <Link
        to="/tools"
        className={clsx(
          'flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] uppercase tracking-wide',
          location.pathname === '/tools'
            ? 'text-accent'
            : 'text-ink-dim hover:bg-bg-soft hover:text-ink',
        )}
      >
        <ArrowLeft className="h-3 w-3" />
        工具广场
      </Link>

      {cat ? <CategoryToolList categoryId={cat.id} /> : <CategoryList />}
    </div>
  );
}

function CategoryList(): JSX.Element {
  return (
    <div className="mt-1 space-y-0.5">
      {categories.map((c) => {
        const Icon = c.icon;
        return (
          <NavLink
            key={c.id}
            to={`/tools/${c.id}`}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-2 rounded-md px-2 py-1 text-[12px] transition',
                isActive
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
              )
            }
            title={c.description}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{c.shortLabel}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

function CategoryToolList({ categoryId }: { categoryId: ToolDef['category'] }): JSX.Element {
  const cat = getCategory(categoryId);
  const list = getToolsByCategory(categoryId);
  if (!cat) return <CategoryList />;
  const Icon = cat.icon;

  return (
    <div className="mt-1">
      <NavLink
        to={`/tools/${cat.id}`}
        end
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-2 rounded-md px-2 py-1 text-[12px] font-medium',
            isActive
              ? 'bg-accent-soft text-accent'
              : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
          )
        }
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{cat.shortLabel}</span>
        <span className="ml-auto text-[10px] text-ink-dim">{list.length}</span>
      </NavLink>

      {list.length === 0 ? (
        <p className="mx-2 mt-1 text-[11px] text-ink-dim">暂无工具</p>
      ) : (
        <div className="ml-2 mt-0.5 space-y-0.5 border-l border-line/60 pl-2">
          {list.map((tool) => {
            const ToolIcon = tool.icon;
            const isAvailable = tool.status === 'available';
            return (
              <NavLink
                key={tool.id}
                to={tool.path}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px] transition',
                    isActive && isAvailable
                      ? 'bg-accent-soft text-accent'
                      : isActive
                        ? 'bg-bg-soft text-ink'
                        : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
                    !isAvailable && 'opacity-70',
                  )
                }
                title={tool.description}
              >
                <ToolIcon className="h-3 w-3" />
                <span className="min-w-0 flex-1 truncate">{tool.name}</span>
                {!isAvailable && (
                  <Lock className="h-2.5 w-2.5 text-ink-dim" />
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
